import React, { useState } from 'react';
import { Plus, X, Calendar, Search, ChevronDown } from 'lucide-react';

export default function CreateOrderPage() {
  const [orderItems, setOrderItems] = useState([
    { id: 1, name: '', price: '', rentalDate: '', returnDate: '', discount: 0 }
  ]);
  const [customer, setCustomer] = useState('');
  const [vendor, setVendor] = useState('');
  const [method, setMethod] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);

  const customers = ['Mohamed Elsayed Ahmed', 'Ahmed Ali', 'Sara Mohamed'];
  const vendors = ['Royal Events', 'Property', 'Elite Real Estate'];
  const paymentMethods = ['Cash', 'Credit Card', 'Bank Transfer', 'PayPal'];

  const addOrderItem = () => {
    setOrderItems([
      ...orderItems,
      { id: Date.now(), name: '', price: '', rentalDate: '', returnDate: '', discount: 0 }
    ]);
  };

  const removeOrderItem = (id) => {
    if (orderItems.length > 1) {
      setOrderItems(orderItems.filter(item => item.id !== id));
    }
  };

  const updateOrderItem = (id, field, value) => {
    setOrderItems(orderItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const calculateSubtotal = () => {
    return orderItems.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      const discount = parseFloat(item.discount) || 0;
      return sum + (price - (price * discount / 100));
    }, 0);
  };

  const subtotal = calculateSubtotal();
  const shipping = 0;
  const totalDiscount = orderItems.reduce((sum, item) => {
    const price = parseFloat(item.price) || 0;
    const discount = parseFloat(item.discount) || 0;
    return sum + (price * discount / 100);
  }, 0);
  const total = subtotal + shipping;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">New Order</h1>
        <p className="text-sm text-gray-500">Dashboard / Orders / New Order</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer & Vendor Info */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Customer & Vendor Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Customer */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
                <div className="relative">
                  <input
                    type="text"
                    value={customer}
                    onChange={(e) => setCustomer(e.target.value)}
                    onFocus={() => setShowCustomerDropdown(true)}
                    placeholder="Select Customer"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                  <ChevronDown className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                </div>
                {showCustomerDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                    {customers.filter(c => c.toLowerCase().includes(customer.toLowerCase())).map((c, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          setCustomer(c);
                          setShowCustomerDropdown(false);
                        }}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                      >
                        {c}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Vendor */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">Vendor</label>
                <div className="relative">
                  <input
                    type="text"
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                    onFocus={() => setShowVendorDropdown(true)}
                    placeholder="Select Vendor"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                  <ChevronDown className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                </div>
                {showVendorDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                    {vendors.filter(v => v.toLowerCase().includes(vendor.toLowerCase())).map((v, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          setVendor(v);
                          setShowVendorDropdown(false);
                        }}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                      >
                        {v}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Payment Method */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                >
                  <option value="">Select Payment Method</option>
                  {paymentMethods.map((m, i) => (
                    <option key={i} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Order Items</h2>
              <button
                onClick={addOrderItem}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </button>
            </div>

            <div className="space-y-4">
              {orderItems.map((item, index) => (
                <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-4">
                    <span className="text-sm font-medium text-gray-600">Item #{index + 1}</span>
                    {orderItems.length > 1 && (
                      <button
                        onClick={() => removeOrderItem(item.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Item Name</label>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => updateOrderItem(item.id, 'name', e.target.value)}
                        placeholder="e.g. royal1"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
                      <input
                        type="number"
                        value={item.price}
                        onChange={(e) => updateOrderItem(item.id, 'price', e.target.value)}
                        placeholder="0.00"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Discount (%)</label>
                      <input
                        type="number"
                        value={item.discount}
                        onChange={(e) => updateOrderItem(item.id, 'discount', e.target.value)}
                        placeholder="0"
                        min="0"
                        max="100"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Rental Date</label>
                      <div className="relative">
                        <input
                          type="datetime-local"
                          value={item.rentalDate}
                          onChange={(e) => updateOrderItem(item.id, 'rentalDate', e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        />
                        <Calendar className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Return Date</label>
                      <div className="relative">
                        <input
                          type="datetime-local"
                          value={item.returnDate}
                          onChange={(e) => updateOrderItem(item.id, 'returnDate', e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        />
                        <Calendar className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Summary Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm p-6 sticky top-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-6">Order Summary</h2>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium text-gray-800">
                  ${(subtotal + totalDiscount).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Shipping</span>
                <span className="font-medium text-gray-800">${shipping.toFixed(2)}</span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Discount</span>
                <span className="font-medium text-red-500">-${totalDiscount.toFixed(2)}</span>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between">
                  <span className="text-base font-semibold text-gray-800">Total</span>
                  <span className="text-lg font-bold text-gray-900">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium">
                Create Order
              </button>
              <button className="w-full py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                Save as Draft
              </button>
              <button className="w-full py-3 text-gray-600 hover:text-gray-800 transition-colors font-medium">
                Cancel
              </button>
            </div>

            {/* Order Status Preview */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">Order Status</label>
              <select className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none">
                <option value="pending">Pending</option>
                <option value="shipped">Shipped</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}